import { useContext, useCallback } from 'react'
import { AppContext, AppContextType } from '../AppContext'
import { RoomContext, RoomContextType } from '../RoomContext'
import useRoomContextRef from './useRoomContextRef'
import useRoomViews from './useRoomViews'

export type ScreenControlRequestWarning = {
    canControl: boolean
    showUnsupportedClientWarning?: boolean
    showVersionWarning?: boolean
}

function useRemoteDesktopControlPermissionActions() {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state: roomState, dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { roomStateRef } = useRoomContextRef()

    const { presentingPinnedPeerSessionId } = useRoomViews()

    const isSessionIdControllingMyDesktop = useCallback(
        (sessionId: string) => {
            const s = appState.appState.remoteDesktopBeingControlledStatus
            return s?.sessionId === sessionId
        },
        [appState.appState.remoteDesktopBeingControlledStatus]
    )

    const screenControlRequestWarning: ScreenControlRequestWarning = (() => {
        const manifest =
            roomState.peers[presentingPinnedPeerSessionId || '']?.manifest?.localP2pDesktop ||
            roomState.peers[presentingPinnedPeerSessionId || '']?.manifest?.localSfuDesktop ||
            null

        if (manifest) {
            // The fact that the client even has the `manifest` field
            // indicates that the client's version is sufficient. This field was
            // introduced in v1.4.10.
            if (manifest.info?.clientKind === 'browser') {
                return { canControl: false, showUnsupportedClientWarning: true }
            } else if (manifest.info?.screenIndex == null) {
                // then is a window, not a screen; screen share is not allowed.
                return { canControl: false }
            } else {
                return { canControl: true }
            }
        } else {
            // client version is less than v1.4.10.
            return {
                canControl: true, // the default solution
                showVersionWarning: true,
            }
        }
    })()

    const showRemoteDesktopControlQuery = () => {
        const peer = roomState.peers[presentingPinnedPeerSessionId || ''] || null
        if (peer) {
            roomDispatch({
                type: 'update_room_context',
                value: {
                    isRemoteDesktopControlQueryPromptOpen: { sessionId: peer.sessionId, username: peer.username },
                },
            })
        }
    }

    const ask = useCallback(
        ({ peerSessionId }: { peerSessionId: string }) => {
            const screenControlRequestNext = {
                ...roomStateRef.current.screenControlRequest,
                receiverSessionId: peerSessionId,
                op: 'start' as '' | 'stop' | 'start',
            }
            screenControlRequestNext.reqId += 1

            roomDispatch({
                type: 'update_room_context',
                value: { screenControlRequest: screenControlRequestNext },
            })
        },
        [roomDispatch, roomStateRef]
    )

    const showScreenControlNotificationPrompt = useCallback(() => {
        appDispatch({
            type: 'new_message_dialog',
            value: {
                id: 'screen_control_update_notification_01',
                text:
                    'If you encounter problems with screen control, please make sure that the app ' +
                    'is up to date, for both the user that controls the screen and for the user whose screen ' +
                    'is being controlled.',
                negativeButtonDoNotShowAgain: true,
            },
        })
    }, [appDispatch])

    const allowOrDeny = useCallback(
        ({
            sessionId,
            username,
            reqId,
            status,
        }: {
            sessionId: string
            username: string
            reqId: number
            status: 'allowed' | 'denied'
        }) => {
            if (!sessionId || reqId == null) {
                return
            }

            let responseNext = { ...roomStateRef.current.lastScreenControlRequestResponse }

            if (status === 'allowed') {
                responseNext = {
                    reqId,
                    status: 'allowed',
                    requesterSessionId: sessionId,
                }

                showScreenControlNotificationPrompt()
            } else {
                responseNext = {
                    reqId,
                    status: 'denied',
                    requesterSessionId: sessionId,
                }
            }

            const screenControlRequestResponseHistoryNext = [
                ...roomStateRef.current.screenControlRequestResponseHistory,
                responseNext,
            ]

            if (status === 'allowed') {
                appDispatch({
                    type: 'update_app_state',
                    value: {
                        remoteDesktopBeingControlledStatus: {
                            username: username,
                            sessionId: sessionId,
                        },
                    },
                })
            }

            roomDispatch({
                type: 'update_room_context',
                value: {
                    lastScreenControlRequestResponse: responseNext,
                    screenControlRequestResponseHistory: screenControlRequestResponseHistoryNext,
                    isRemoteDesktopControlRequestPromptOpen: null,
                },
            })
        },
        [roomDispatch, roomStateRef, showScreenControlNotificationPrompt]
    )

    const revoke = useCallback(() => {
        roomDispatch({
            type: 'update_room_context',
            value: {
                lastScreenControlRequestResponse: {
                    ...roomStateRef.current.lastScreenControlRequestResponse,
                    status: 'revoked',
                },
            },
        })

        appDispatch({
            type: 'update_app_state',
            value: {
                remoteDesktopBeingControlledStatus: null,
            },
        })
    }, [roomDispatch, roomStateRef])

    const stop = useCallback(({ peerSessionId }: { peerSessionId: string }) => {
        const screenControlRequestNext = {
            ...roomStateRef.current.screenControlRequest,
            receiverSessionId: peerSessionId,
            op: 'stop' as '' | 'stop' | 'start',
        }
        screenControlRequestNext.reqId += 1

        roomDispatch({
            type: 'update_room_context',
            value: {
                remoteDesktopControlStatus: null,
                screenControlRequest: screenControlRequestNext,
            },
        })
    }, [])

    return {
        ask,
        allowOrDeny,
        revoke,
        stop,

        isSessionIdControllingMyDesktop,
        screenControlRequestWarning,
        showRemoteDesktopControlQuery,
    }
}

export default useRemoteDesktopControlPermissionActions
